import React from 'react';

/**
 * UFC-style fighter card component
 * Displays full fighter profile with stats, record, and attributes
 */
export function FighterCard({ profile, className = '' }) {
  if (!profile) {
    return (
      <div className={`bg-gray-800 rounded-lg p-6 ${className}`}>
        <p className="text-gray-400 text-center">No fighter profile found</p>
      </div>
    );
  }

  const {
    fighter_name,
    tagline,
    fighting_style,
    hometown,
    record_wins,
    record_losses,
    record_draws,
    height_cm,
    weight_kg,
    reach_cm,
    power_rating,
    endurance_rating,
    consistency_rating,
    profile_image_url,
    current_title_id,
    total_rivalries,
    championship_wins,
  } = profile;

  // Calculate record string
  const record = `${record_wins}-${record_losses}-${record_draws}`;

  // Format height (cm to ft/in for display)
  const heightFt = height_cm ? Math.floor(height_cm / 30.48) : null;
  const heightIn = height_cm ? Math.round((height_cm % 30.48) / 2.54) : null;
  const heightDisplay = heightFt && heightIn ? `${heightFt}'${heightIn}"` : 'N/A';

  // Format weight
  const weightDisplay = weight_kg ? `${Math.round(weight_kg)} kg` : 'N/A';
  const reachDisplay = reach_cm ? `${reach_cm} cm` : 'N/A';

  return (
    <div className={`bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg shadow-2xl overflow-hidden ${className}`}>
      {/* Banner Section */}
      <div className="relative h-32 bg-gradient-to-r from-red-600 to-orange-600">
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="absolute bottom-4 left-4 right-4">
          <h1 className="text-3xl font-bold text-white drop-shadow-lg">{fighter_name}</h1>
          {tagline && <p className="text-sm text-gray-200 italic mt-1">{tagline}</p>}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Profile Image */}
          <div className="col-span-1">
            <div className="relative">
              {profile_image_url ? (
                <img
                  src={profile_image_url}
                  alt={fighter_name}
                  className="w-full h-64 object-cover rounded-lg border-4 border-gray-700"
                />
              ) : (
                <div className="w-full h-64 bg-gray-700 rounded-lg border-4 border-gray-700 flex items-center justify-center">
                  <svg className="w-24 h-24 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
              )}

              {/* Current Title Badge */}
              {current_title_id && (
                <div className="absolute top-2 left-2 bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-bold">
                  CHAMPION
                </div>
              )}
            </div>

            {/* Record */}
            <div className="mt-4 bg-gray-800 rounded-lg p-4 text-center">
              <p className="text-gray-400 text-sm mb-1">Record</p>
              <p className="text-3xl font-bold text-white">{record}</p>
              <p className="text-xs text-gray-500 mt-1">Win - Loss - Draw</p>
            </div>
          </div>

          {/* Middle Column - Stats & Info */}
          <div className="col-span-1 md:col-span-2">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-800 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Fighting Style</p>
                <p className="text-white font-semibold mt-1">{fighting_style || 'Unspecified'}</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Hometown</p>
                <p className="text-white font-semibold mt-1">{hometown || 'Unknown'}</p>
              </div>
            </div>

            {/* Physical Stats */}
            <div className="mb-6">
              <h3 className="text-white font-bold mb-3">Physical Stats</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-gray-400 text-xs mb-1">Height</p>
                  <p className="text-white font-bold text-lg">{heightDisplay}</p>
                  <p className="text-gray-500 text-xs">{height_cm ? `${height_cm} cm` : ''}</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-gray-400 text-xs mb-1">Weight</p>
                  <p className="text-white font-bold text-lg">{weightDisplay}</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-gray-400 text-xs mb-1">Reach</p>
                  <p className="text-white font-bold text-lg">{reachDisplay}</p>
                </div>
              </div>
            </div>

            {/* Attributes */}
            <div className="mb-6">
              <h3 className="text-white font-bold mb-3">Attributes</h3>
              <div className="space-y-3">
                {/* Power */}
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-400 text-sm">Power</span>
                    <span className="text-white font-semibold">{power_rating}/100</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-red-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${power_rating}%` }}
                    ></div>
                  </div>
                </div>

                {/* Endurance */}
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-400 text-sm">Endurance</span>
                    <span className="text-white font-semibold">{endurance_rating}/100</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${endurance_rating}%` }}
                    ></div>
                  </div>
                </div>

                {/* Consistency */}
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-400 text-sm">Consistency</span>
                    <span className="text-white font-semibold">{consistency_rating}/100</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${consistency_rating}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Career Stats */}
            <div>
              <h3 className="text-white font-bold mb-3">Career Stats</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-gray-400 text-xs">Total Rivalries</p>
                  <p className="text-white font-bold text-2xl mt-1">{total_rivalries}</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-gray-400 text-xs">Championship Wins</p>
                  <p className="text-white font-bold text-2xl mt-1">{championship_wins}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact version of fighter card for leaderboards
 */
export function CompactFighterCard({ profile, rank, points, className = '' }) {
  if (!profile) return null;

  const { fighter_name, record_wins, record_losses, profile_image_url } = profile;

  return (
    <div className={`flex items-center bg-gray-800 rounded-lg p-4 ${className}`}>
      {/* Rank */}
      <div className="flex-shrink-0 w-12 text-center">
        <div
          className={`text-2xl font-bold ${
            rank === 1 ? 'text-yellow-400' : rank === 2 ? 'text-gray-300' : rank === 3 ? 'text-orange-400' : 'text-gray-500'
          }`}
        >
          #{rank}
        </div>
      </div>

      {/* Avatar */}
      <div className="flex-shrink-0 ml-4">
        {profile_image_url ? (
          <img src={profile_image_url} alt={fighter_name} className="w-12 h-12 rounded-full object-cover border-2 border-gray-600" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center border-2 border-gray-600">
            <svg className="w-6 h-6 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          </div>
        )}
      </div>

      {/* Name & Record */}
      <div className="flex-grow ml-4">
        <p className="text-white font-semibold">{fighter_name}</p>
        <p className="text-gray-400 text-sm">
          {record_wins}-{record_losses}
        </p>
      </div>

      {/* Points */}
      <div className="flex-shrink-0 text-right">
        <p className="text-2xl font-bold text-white">{points}</p>
        <p className="text-xs text-gray-400">points</p>
      </div>
    </div>
  );
}

/**
 * Mini card for chat messages and notifications
 */
export function MiniFighterCard({ profile, subtitle, className = '' }) {
  if (!profile) return null;

  const { fighter_name, profile_image_url } = profile;

  return (
    <div className={`flex items-center ${className}`}>
      {/* Avatar */}
      {profile_image_url ? (
        <img src={profile_image_url} alt={fighter_name} className="w-8 h-8 rounded-full object-cover" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
          <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>
        </div>
      )}

      {/* Name */}
      <div className="ml-2">
        <p className="text-white font-semibold text-sm">{fighter_name}</p>
        {subtitle && <p className="text-gray-400 text-xs">{subtitle}</p>}
      </div>
    </div>
  );
}
